import React, { useEffect, useState, useCallback } from 'react';
import { updateForm, getFormAuths, addFormAuth, deleteFormAuth } from '../../api/aprvForms';
import OrgSelect from '../../components/OrgSelect/OrgSelect';

export default function AprvFormsEdit({ form, onCancel, token, onRefresh, folderOptions }) {
  const [formData, setFormData] = useState({
    form_code: form.form_code,
    form_name: form.form_name,
    parent_code: form.parent_code || '',
    form_type: form.form_type,
    sub_form_code: form.sub_form_code || '',
    sort_order: form.sort_order ?? 0,
    use_yn: form.use_yn,
    auth_role: form.auth_role,
  });

  const [auths, setAuths] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showOrgPopup, setShowOrgPopup] = useState(false);

  const refreshAuths = useCallback(() => {
    getFormAuths(form.form_code, token)
      .then(res => setAuths(Array.isArray(res) ? res : []))
      .catch(() => setAuths([]));
  }, [form.form_code, token]);

  useEffect(() => {
    refreshAuths();
  }, [refreshAuths]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateForm(form.form_code, formData, token);

      // ✅ 여기서 auths를 서버에 반영
      // 기존 권한을 모두 삭제 후 새 auths를 추가하는 방식
      const originalAuths = await getFormAuths(form.form_code, token);
      for (const a of originalAuths) {
        await deleteFormAuth(form.form_code, a.target_type, a.target_id, token);
      }
      for (const a of auths) {
        await addFormAuth(form.form_code, a, token);
      }

      alert('양식 및 권한이 수정되었습니다');
      onCancel();
      onRefresh();
    } catch (err) {
      console.error('양식 수정 실패:', err);
      alert('양식 수정 중 오류가 발생했습니다');
    } finally {
      setSaving(false);
    }
  };


  // ✅ OrgSelect에서 확인 버튼 눌렀을 때
  const handleConfirmOrgSelection = async (selectedTargets) => {
    // ❌ 바로 addFormAuth 호출하지 않음
    setAuths(selectedTargets); // ✅ 로컬 상태만 업데이트
    setShowOrgPopup(false);
  };


  return (
    <div className="d-flex flex-column gap-3">
      {/* 양식 수정 */}
      <div className="card">
        <div className="card-header">✏️ 결재양식 수정</div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="form-label">코드</label>
              <input type="text" name="form_code" className="form-control"
                value={formData.form_code} disabled />
            </div>
            <div className="mb-2">
              <label className="form-label">이름</label>
              <input type="text" name="form_name" className="form-control"
                value={formData.form_name || ''} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">상위 코드(parent_code)</label>
              <select
                name="parent_code"
                className="form-select"
                value={formData.parent_code || ''}
                onChange={handleChange}
              >
                {folderOptions.map(opt => (
                  <option key={opt.code} value={opt.code}>
                    {opt.name} ({opt.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">유형(form_type)</label>
              <select name="form_type" className="form-select"
                value={formData.form_type} onChange={handleChange}>
                <option value="folder">폴더</option>
                <option value="form">양식</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">하위 양식 코드(sub_form_code)</label>
              <input type="text" name="sub_form_code" className="form-control"
                value={formData.sub_form_code || ''} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label className="form-label">정렬 순서(sort_order)</label>
              <input type="number" name="sort_order" className="form-control"
                value={formData.sort_order ?? 0} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label className="form-label">사용 여부(use_yn)</label>
              <select name="use_yn" className="form-select"
                value={formData.use_yn} onChange={handleChange}>
                <option value="Y">사용</option>
                <option value="N">미사용</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">권한 역할(auth_role)</label>
              <select name="auth_role" className="form-select"
                value={formData.auth_role} onChange={handleChange}>
                <option value="all">전체</option>
                <option value="admin">관리자</option>
                <option value="manager">매니저</option>
              </select>
            </div>

            {/* ✅ 권한 관리 섹션 */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label mb-0">권한 관리</label>
                <button type="button" className="btn btn-primary" onClick={() => setShowOrgPopup(true)}>
                  권한 추가
                </button>
              </div>

              {Array.isArray(auths) && auths.length > 0 ? (
                <ul className="list-group mb-2">
                  {auths.map(a => (
                    <li
                      key={`${a.form_code}-${a.target_type}-${a.target_id}`}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <strong>{a.target_type === 'user' ? '사용자' : '부서'}:</strong> {a.target_name || a.target_id}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          // ❌ 바로 deleteFormAuth 호출하지 않음
                          setAuths(auths.filter(x => !(x.target_type === a.target_type && x.target_id === a.target_id)));
                        }}
                      >
                        삭제
                      </button>

                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted mb-2">등록된 권한이 없습니다</div>
              )}
            </div>

            {/* 수정/취소 버튼 */}
            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-warning me-2" disabled={saving}>
                {saving ? '저장 중...' : '수정'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                취소
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ✅ OrgSelect 레이어 팝업 */}
      {showOrgPopup && (
        <OrgSelect
          onConfirm={handleConfirmOrgSelection}
          onCancel={() => setShowOrgPopup(false)}
          selectionMode="multiple"
          targetType="all"
          initialSelected={auths.map(a => ({
            target_type: a.target_type,
            target_id: a.target_id,
            target_name: a.target_name || a.target_id
          }))}
        />
      )}
    </div>
  );
}